import { useState, type ReactNode } from "react";
import { Switch, Tooltip, message } from "antd";
import {
  ensurePushSubscription,
  isPushPermissionDenied,
  isPushSupported,
} from "@/utils/pushNotifications";

export type AlertNotificationChannel = "notify_email" | "notify_push";

type AlertNotificationTogglesProps = {
  notifyEmail: boolean;
  notifyPush: boolean;
  disabled?: boolean;
  disabledReason?: string;
  onChange: (channel: AlertNotificationChannel, value: boolean) => void;
};

export default function AlertNotificationToggles({
  notifyEmail,
  notifyPush,
  disabled = false,
  disabledReason,
  onChange,
}: AlertNotificationTogglesProps) {
  const [messageApi, contextHolder] = message.useMessage();
  const [enablingPush, setEnablingPush] = useState(false);

  const handlePushChange = async (checked: boolean) => {
    if (!checked) {
      onChange("notify_push", false);
      return;
    }
    if (!isPushSupported()) {
      messageApi.error("This browser does not support push notifications.");
      return;
    }
    if (isPushPermissionDenied()) {
      messageApi.error(
        "Notifications are blocked for this site. Allow them in your browser settings first.",
      );
      return;
    }
    setEnablingPush(true);
    try {
      const subscribed = await ensurePushSubscription();
      if (!subscribed) {
        messageApi.error("Notification permission was not granted.");
        return;
      }
      onChange("notify_push", true);
    } catch (error) {
      console.error("Error enabling push notifications:", error);
      messageApi.error(
        "Could not enable browser notifications. Please try again.",
      );
    } finally {
      setEnablingPush(false);
    }
  };

  const withDisabledReason = (node: ReactNode) =>
    disabled && disabledReason ? (
      <Tooltip title={disabledReason}>{node}</Tooltip>
    ) : (
      node
    );

  return (
    <div data-testid="alert-notification-toggles">
      {contextHolder}
      <div className="flex items-center justify-between gap-2 py-1">
        <span className="text-sm text-[#2B4752]">Email me when triggered</span>
        {withDisabledReason(
          <Switch
            size="small"
            data-testid="alert-notify-email-switch"
            checked={notifyEmail}
            disabled={disabled}
            onChange={(checked) => onChange("notify_email", checked)}
          />,
        )}
      </div>
      <div className="flex items-center justify-between gap-2 py-1">
        <span className="text-sm text-[#2B4752]">
          Browser notification when triggered
        </span>
        {withDisabledReason(
          <Switch
            size="small"
            data-testid="alert-notify-push-switch"
            checked={notifyPush}
            disabled={disabled}
            loading={enablingPush}
            onChange={handlePushChange}
          />,
        )}
      </div>
    </div>
  );
}
